import React from 'react'
import styled, { keyframes } from 'styled-components'
import { DSButton, DSDivider, DSSpacer, DSText } from '@zoe/ds-web'
import { onNextClickType, ScreenProps } from '../types'
import { appConfig } from '../../appConfig'
import { ContentContainerMax } from '../core/styled/ContentContainer'
import { useFocusOnMount } from '../../hooks/useFocusOnMount'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { SCREEN_MAX_WIDTH } from '../core/constants'

const Main = styled.main`
  display: grid;
  grid-template-columns: 1fr;
`

const CenteredRow = styled.div`
  display: flex;
  justify-content: center;
`

const ExplanationContainer = styled(ContentContainerMax)<{ $tightTop?: boolean; $tightFooter?: boolean }>`
  margin-top: ${({ $tightTop }) => ($tightTop ? '0' : 'var(--grid-40)')};
  margin-bottom: var(--grid-8);
  margin-left: 0;
  margin-right: 0;
  /* Pull the footer (e.g. a source citation) tight under the body copy. */
  ${({ $tightFooter }) => ($tightFooter ? 'padding-bottom: 0;' : '')}
`

const FooterContainer = styled(ContentContainerMax)`
  margin-left: 0;
  margin-right: 0;
  padding-top: 0;
`

const imgFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`

const CenteredImage = styled.img.attrs({ loading: 'lazy' })<{ $bleed?: boolean }>`
  display: block;
  width: 100%;
  object-fit: cover;
  border-radius: var(--radius-16, 16px);
  overflow: hidden;
  animation: ${imgFadeIn} 0.3s ease-out both;

  /* Full width on phones: escapes ContentContainerMax's 24px side padding. */
  ${({ $bleed }) =>
    $bleed
      ? `border-radius: 0;
         @media (max-width: ${SCREEN_MAX_WIDTH}px) {
           width: calc(100% + 2 * var(--grid-24));
           margin-left: calc(-1 * var(--grid-24));
         }`
      : ''}
`

// Holds the image plus an overlay (e.g. an animated Ziggie), clipped to the
// image's box. Takes over the bleed so image and overlay move together.
const ImageFrame = styled.div<{ $bleed?: boolean }>`
  position: relative;
  overflow: hidden;

  ${({ $bleed }) =>
    $bleed
      ? `@media (max-width: ${SCREEN_MAX_WIDTH}px) {
           width: calc(100% + 2 * var(--grid-24));
           margin-left: calc(-1 * var(--grid-24));
         }`
      : ''}
`

const AttributionImage = styled.img.attrs({ loading: 'lazy' })`
  max-width: 280px;
  margin: 0 auto;
`

const ExternalLink = styled.a`
  color: var(--colour-text-link);
  display: flex;
  align-items: flex-start;
  margin-top: var(--grid-32);
`

const HomepageLink = styled.a`
  margin-top: var(--grid-16);
  display: block;
  color: var(--colour-text-link);
`

type CTAStyle = 'button' | 'link'

type Link = {
  url: string
  text: string
}

export interface ExplanationTemplateProps extends ScreenProps {
  headerBar?: React.ReactNode
  image?: string
  imageAspectRatio?: string
  descriptionBelowImage?: React.ReactNode
  imageAlt?: string
  title: React.ReactNode
  subTitle?: React.ReactNode
  body?: React.ReactNode
  footerTitle?: React.ReactNode
  footerBody?: React.ReactNode
  healthStat?: React.ReactNode
  attribution?: React.ReactNode
  attributionImage?: string
  attributionImageAlt?: string
  showHomepageLink?: boolean
  subtitleAboveTitle?: boolean
  titleAboveImage?: boolean
  imageObjectFit?: 'cover' | 'contain'
  centerText?: boolean
  // When true, removes the 40px gap above the title/image. Used by screens
  // where the content should sit close to the progress bar (e.g. symptom-intro).
  tightTop?: boolean
  // When true, collapses the gap between the body copy and the footer (e.g. a
  // source citation), so the source sits tight under the paragraph.
  tightFooter?: boolean
  // When true, reduces the gap between the image and the content below it.
  tightImage?: boolean
  // Square corners for images whose content runs to the edge (e.g. the e10/e11 graph
  // images), where the default 16px radius would clip it.
  squareImage?: boolean
  // e10/e11: 14px regular source/footer line instead of the default light 16px.
  regularFooter?: boolean
  // e10/e11: 36px title instead of the default 30px.
  largeTitle?: boolean
  // e12: image runs edge to edge on phones.
  imageBleed?: boolean
  // e12: shown under the title and subtitle, e.g. the ratings row.
  belowTitle?: React.ReactNode
  // e12: layered on top of the image, positioned in % of the image's box.
  imageOverlay?: React.ReactNode
  hideContinueButton?: boolean
  cta?: React.ReactNode
  ctaStyle?: CTAStyle
  externalLinks?: Link[]
}

export const ExplanationTemplate: React.FC<ExplanationTemplateProps> = ({
  headerBar,
  image,
  imageAspectRatio,
  descriptionBelowImage,
  imageAlt,
  title,
  subTitle,
  subtitleAboveTitle,
  titleAboveImage,
  imageObjectFit,
  centerText,
  tightTop,
  tightFooter,
  tightImage,
  squareImage,
  regularFooter,
  largeTitle,
  imageBleed,
  belowTitle,
  imageOverlay,
  body,
  footerTitle,
  footerBody,
  healthStat,
  attribution,
  attributionImage,
  attributionImageAlt,
  cta,
  ctaStyle = 'button',
  showHomepageLink,
  hideContinueButton,
  onNextClick,
  externalLinks,
}) => {
  const mainRef = useFocusOnMount<HTMLElement>()
  const titleVariant = largeTitle ? 'fluid-heading-700' : 'fluid-heading-600'

  const showFooter = !!(footerBody || footerTitle || attribution || attributionImage)
  return (
    <div>
      {headerBar}
      <Main ref={mainRef} tabIndex={-1} style={{ outline: 'none' }}>
        <CenteredRow>
          <ExplanationContainer $tightTop={tightTop} $tightFooter={showFooter ? tightFooter : undefined}>
            {titleAboveImage && title && (
              <>
                <DSText variant={titleVariant} weight="semiBold" align={centerText ? 'center' : 'left'}>
                  {title}
                </DSText>
                <DSSpacer size={24} direction="vertical" />
              </>
            )}
            {image && imageOverlay && (
              <ImageFrame $bleed={imageBleed}>
                <CenteredImage
                  src={`${image}`}
                  alt={imageAlt}
                  style={{
                    ...(imageAspectRatio ? { aspectRatio: imageAspectRatio } : {}),
                    ...(imageObjectFit ? { objectFit: imageObjectFit } : {}),
                    ...(squareImage || imageBleed ? { borderRadius: 0 } : {}),
                  }}
                />
                {imageOverlay}
              </ImageFrame>
            )}
            {image && !imageOverlay && (
              <CenteredImage
                $bleed={imageBleed}
                src={`${image}`}
                alt={imageAlt}
                style={{
                  ...(imageAspectRatio ? { aspectRatio: imageAspectRatio } : {}),
                  ...(imageObjectFit ? { objectFit: imageObjectFit } : {}),
                  ...(squareImage ? { borderRadius: 0 } : {}),
                }}
              />
            )}
            {image && <DSSpacer size={tightImage ? 8 : 24} direction="vertical" />}
            {descriptionBelowImage && (
              <>
                <DSText variant="fluid-paragraph-200" weight="light">
                  {descriptionBelowImage}
                </DSText>
                <DSSpacer size={40} direction="vertical" />
                <DSDivider />
                <DSSpacer size={40} direction="vertical" />
              </>
            )}
            {subTitle && subtitleAboveTitle && (
              <>
                <DSText variant="fluid-paragraph-300" color="secondary" align={centerText ? 'center' : 'left'}>
                  {subTitle}
                </DSText>
                <DSSpacer size={8} direction="vertical" />
              </>
            )}
            {!titleAboveImage && title && (
              <DSText variant={titleVariant} weight="semiBold" align={centerText ? 'center' : 'left'}>
                {title}
              </DSText>
            )}
            {subTitle && !subtitleAboveTitle && (
              <>
                <DSSpacer size={8} direction="vertical" />
                <DSText variant="fluid-paragraph-300" color="secondary" align={centerText ? 'center' : 'left'}>
                  {subTitle}
                </DSText>
              </>
            )}
            {belowTitle && (
              <>
                <DSSpacer size={24} direction="vertical" />
                {belowTitle}
              </>
            )}
            {body && (
              <>
                <DSSpacer size={24} direction="vertical" />
                <DSText variant="fluid-paragraph-200" weight="light">
                  {body}
                </DSText>
              </>
            )}
            {!showFooter && !healthStat && !hideContinueButton && (
              <ExplanationCTA cta={cta} ctaStyle={ctaStyle} onNextClick={onNextClick} />
            )}
            {externalLinks && (
              <div>
                {externalLinks.map((link) => (
                  <ExternalLink href={link.url} target="_blank" rel="noopener noreferrer" key={link.text}>
                    {link.text}
                  </ExternalLink>
                ))}
                <br />
              </div>
            )}
            {showHomepageLink && (
              <HomepageLink href={`${appConfig.zoeWebsiteBaseUri}`} data-testid="homepage-link">
                Return to homepage
              </HomepageLink>
            )}
          </ExplanationContainer>
        </CenteredRow>
        {showFooter && (
          <CenteredRow>
            <FooterContainer>
              {footerTitle && (
                <>
                  <DSText variant="fluid-heading-600" weight="semiBold">
                    {footerTitle}
                  </DSText>
                  <DSSpacer size={16} direction="vertical" />
                </>
              )}
              {footerBody && (
                <DSText
                  variant={regularFooter ? 'fluid-label-100' : 'fluid-paragraph-200'}
                  weight={regularFooter ? 'regular' : 'light'}
                  color={regularFooter ? 'placeholder' : 'secondary'}
                >
                  {footerBody}
                </DSText>
              )}
              {!hideContinueButton && <ExplanationCTA cta={cta} ctaStyle={ctaStyle} onNextClick={onNextClick} />}
              {attribution && (
                <>
                  <DSSpacer size={32} direction="vertical" />
                  <DSText variant="fluid-paragraph-200" weight="light" color="secondary">
                    {attribution}
                  </DSText>
                  <DSSpacer size={32} direction="vertical" />
                </>
              )}
              {attributionImage && (
                <AttributionImage src={`${appConfig.publicUrl}${attributionImage}`} alt={attributionImageAlt} />
              )}
            </FooterContainer>
          </CenteredRow>
        )}
        {healthStat && (
          <div style={{ maxWidth: `${SCREEN_MAX_WIDTH}px`, margin: '0 auto' }}>
            {healthStat}
            {!hideContinueButton && <ExplanationCTA cta={cta} ctaStyle={ctaStyle} onNextClick={onNextClick} />}
          </div>
        )}
      </Main>
    </div>
  )
}

type ExplanationScreenCTAProps = {
  cta: React.ReactNode
  ctaStyle: CTAStyle
  onNextClick: onNextClickType
}
const ExplanationCTA: React.FC<ExplanationScreenCTAProps> = ({ cta, ctaStyle, onNextClick }) => {
  return (
    <>
      <StickyFooter>
        <StickyFooterContent>
          {ctaStyle === 'button' ? (
            <DSButton
              variant="primary"
              colorScheme="default"
              size="large"
              width="full"
              onPress={onNextClick}
              testId="continue-button"
            >
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                {cta}
              </DSText>
            </DSButton>
          ) : (
            <HomePageLink linkText={cta} />
          )}
        </StickyFooterContent>
      </StickyFooter>
    </>
  )
}

type HomePageLinkProps = {
  linkText: React.ReactNode
}
const HomePageLink: React.FC<HomePageLinkProps> = ({ linkText }) => {
  return (
    <HomepageLink href={appConfig.zoeWebsiteBaseUri} data-testid="homepage-link">
      {linkText}
    </HomepageLink>
  )
}
